'use client'

import { useStatsigClient } from '@statsig/react-bindings'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import type { StatsigGateId } from '~/configs/statsig'

import {
  clearGateOverrides,
  getGateOverrides,
  setGateOverride,
  subscribeToGateOverrides,
  type GateOverrides
} from './gateOverrides'

const EMPTY_OVERRIDES: GateOverrides = {}

/** The store is browser-only; SSR renders the empty set. */
function getServerSnapshot(): GateOverrides {
  return EMPTY_OVERRIDES
}

export type UseGateOverridesResult = {
  overrides: GateOverrides
  overriddenCount: number
  setOverride: (gateId: StatsigGateId, value: boolean | null) => void
  resetAll: () => void
}

/**
 * Read and write local gate overrides, re-evaluating every gate consumer in
 * the tree when one changes.
 *
 * The React bindings memoize gate reads on a `renderVersion` that only advances
 * when the client emits `values_updated`. Mutating the override adapter is
 * invisible to that counter, so each write emits the event to invalidate the
 * memos — `useGateValue`, `useStatsigGate` and the `checkGate` handed out by
 * `useStatsigClient` all key off it. The payload is inert: `StatsigProvider`
 * recomputes its loading state from the client rather than from the event.
 */
export default function useGateOverrides(): UseGateOverridesResult {
  const { client } = useStatsigClient()
  const overrides = useSyncExternalStore(subscribeToGateOverrides, getGateOverrides, getServerSnapshot)

  const revalidateGates = useCallback(() => {
    client.$emt({ name: 'values_updated', status: 'Ready', values: null })
  }, [client])

  const setOverride = useCallback(
    (gateId: StatsigGateId, value: boolean | null) => {
      setGateOverride(gateId, value)
      revalidateGates()
    },
    [revalidateGates]
  )

  const resetAll = useCallback(() => {
    clearGateOverrides()
    revalidateGates()
  }, [revalidateGates])

  return useMemo(
    () => ({
      overrides,
      overriddenCount: Object.keys(overrides).length,
      setOverride,
      resetAll
    }),
    [overrides, setOverride, resetAll]
  )
}
